import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Link } from "react-router-dom";
import { Sparkles, Save, Ticket, ExternalLink, Bus } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  getPacchettiConfig,
  setPacchetto,
  updateCarnetOspite,
} from "../../utils/centroDiurnoApi";

function isCarnetPacchetto(pacc, pacchettiCatalogo) {
  if (!pacc) return false;
  if (pacc.is_carnet) return true;
  const def = pacchettiCatalogo.find((p) => p.id === pacc.tipo);
  return Boolean(def?.is_carnet) || pacc.tipo === "carnet_10";
}

export default function CdPacchettoFascicoloSection({
  fascicoloId,
  pacchetto,
  canManage,
  token,
  onRefresh,
}) {
  const [catalogo, setCatalogo] = useState([]);
  const [tipo, setTipo] = useState("");
  const [trasportoAr, setTrasportoAr] = useState(false);
  const [saving, setSaving] = useState(false);
  const [carnetResidui, setCarnetResidui] = useState("");
  const [carnetScadenza, setCarnetScadenza] = useState("");
  const [savingCarnet, setSavingCarnet] = useState(false);

  const pacchettiAttivi = useMemo(
    () => catalogo.filter((p) => p.attivo !== false),
    [catalogo]
  );

  const pacchettoDefSelezionato = useMemo(
    () => pacchettiAttivi.find((p) => p.id === tipo),
    [pacchettiAttivi, tipo]
  );

  const carnetSalvato = useMemo(
    () => isCarnetPacchetto(pacchetto, catalogo),
    [pacchetto, catalogo]
  );

  const carnetSelezionatoDaSalvare = Boolean(
    pacchettoDefSelezionato?.is_carnet && !carnetSalvato
  );

  const loadCatalogo = useCallback(async () => {
    if (!token) return;
    try {
      const cfg = await getPacchettiConfig(token);
      setCatalogo(cfg.pacchetti || []);
    } catch {
      toast.error("Errore caricamento pacchetti Centro Diurno");
    }
  }, [token]);

  useEffect(() => {
    loadCatalogo();
  }, [loadCatalogo]);

  useEffect(() => {
    const p = pacchetto || {};
    setTipo(p.tipo || pacchettiAttivi[0]?.id || "mensile_assistito");
    setTrasportoAr(Boolean(p.trasporto_ar));
    setCarnetResidui(p.carnet_residui != null ? String(p.carnet_residui) : "");
    setCarnetScadenza(p.carnet_scadenza || "");
  }, [pacchetto, pacchettiAttivi]);

  const handleTipoChange = (value) => {
    setTipo(value);
    const def = pacchettiAttivi.find((p) => p.id === value);
    if (def) setTrasportoAr(Boolean(def.trasporto_ar_default));
  };

  const handleSavePacchetto = async () => {
    if (!fascicoloId || !tipo) return;
    setSaving(true);
    try {
      await setPacchetto(token, fascicoloId, {
        tipo,
        trasporto_ar: trasportoAr,
      });
      toast.success("Pacchetto Centro Diurno aggiornato");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio pacchetto");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCarnet = async () => {
    if (!fascicoloId) return;
    setSavingCarnet(true);
    try {
      await updateCarnetOspite(token, fascicoloId, {
        carnet_residui: parseInt(carnetResidui, 10) || 0,
        carnet_scadenza: carnetScadenza || undefined,
      });
      toast.success("Carnet aggiornato");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento carnet");
    } finally {
      setSavingCarnet(false);
    }
  };

  const servizi = pacchetto?.servizi_inclusi || [];
  const residui = pacchetto?.carnet_residui ?? 0;
  const totali = pacchetto?.carnet_ingressi_totali ?? pacchetto?.carnet_residui ?? 10;
  const pct = totali > 0 ? Math.round((residui / totali) * 100) : 0;

  let carnetStato = "ok";
  if (pacchetto?.carnet_scadenza) {
    try {
      const giorni = Math.ceil((parseISO(pacchetto.carnet_scadenza) - new Date()) / (1000 * 60 * 60 * 24));
      if (giorni < 0) carnetStato = "scaduto";
      else if (residui <= 0) carnetStato = "esaurito";
      else if (giorni <= 14 || residui <= 2) carnetStato = "attenzione";
    } catch {
      /* ignore */
    }
  } else if (residui <= 0) {
    carnetStato = "esaurito";
  }

  const carnetStatoLabel = {
    ok: { text: "Attivo", className: "bg-emerald-100 text-emerald-800" },
    attenzione: { text: "In scadenza", className: "bg-amber-100 text-amber-800" },
    esaurito: { text: "Esaurito", className: "bg-red-100 text-red-800" },
    scaduto: { text: "Scaduto", className: "bg-slate-200 text-slate-700" },
  }[carnetStato];

  return (
    <div className="space-y-4 mt-4">
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            Pacchetto Centro Diurno
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500 text-xs">Tipo pacchetto</Label>
              <Select value={tipo} onValueChange={handleTipoChange} disabled={!canManage}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona pacchetto" />
                </SelectTrigger>
                <SelectContent>
                  {pacchettiAttivi.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nome}
                      {p.is_carnet ? ` · ${p.carnet_ingressi} ingressi` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pacchettoDefSelezionato?.descrizione && (
                <p className="text-xs text-slate-500 mt-1.5">{pacchettoDefSelezionato.descrizione}</p>
              )}
            </div>
            <div className="flex flex-col justify-end">
              <div
                className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-3 transition-colors ${
                  trasportoAr ? "bg-emerald-50/70 border-emerald-200" : "bg-slate-50 border-slate-200"
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      trasportoAr ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    <Bus className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium leading-tight ${trasportoAr ? "text-emerald-900" : "text-slate-700"}`}>
                      Trasporto A/R
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">Andata e rientro con pulmino</p>
                  </div>
                </div>
                <Switch
                  checked={trasportoAr}
                  disabled={!canManage}
                  onCheckedChange={setTrasportoAr}
                  className="shrink-0 data-[state=checked]:bg-emerald-600"
                />
              </div>
            </div>
          </div>

          {servizi.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Servizi inclusi</p>
              <div className="flex flex-wrap gap-1.5">
                {servizi.map((s) => (
                  <Badge key={s} variant="secondary" className="text-xs bg-emerald-50 text-emerald-800 border-emerald-100">
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {canManage && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSavePacchetto}
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-1" />
                {saving ? "Salvataggio..." : "Salva pacchetto"}
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/centro-diurno/pacchetti">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Configura pacchetti
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {carnetSelezionatoDaSalvare && (
        <Card className="card-base border-dashed border-amber-200 bg-amber-50/40">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-900">
              Salva il pacchetto per attivare un <strong>Carnet {pacchettoDefSelezionato.carnet_ingressi} ingressi</strong>
              {pacchettoDefSelezionato.carnet_giorni_validita
                ? ` valido ${pacchettoDefSelezionato.carnet_giorni_validita} giorni`
                : ""}
              . Dopo il salvataggio potrai consultare e modificare lo stato del carnet.
            </p>
          </CardContent>
        </Card>
      )}

      {carnetSalvato && pacchetto && (
        <Card className="card-base border-amber-200/80">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Ticket className="w-5 h-5 text-amber-600" />
              Stato Carnet
              <Badge className={carnetStatoLabel.className}>{carnetStatoLabel.text}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="max-w-md">
              <div className="flex justify-between text-sm text-slate-700 mb-1.5">
                <span>
                  <strong>{residui}</strong> ingressi residui
                </span>
                <span className="text-slate-500">su {totali}</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    pct <= 20 ? "bg-red-500" : pct <= 50 ? "bg-amber-500" : "bg-emerald-500"
                  }`}
                  style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                />
              </div>
            </div>

            {pacchetto.carnet_scadenza && (
              <p className="text-sm text-slate-600">
                Scadenza:{" "}
                <strong>{format(parseISO(pacchetto.carnet_scadenza), "d MMMM yyyy", { locale: it })}</strong>
              </p>
            )}

            {canManage && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                <div>
                  <Label className="text-xs">Ingressi residui</Label>
                  <Input
                    type="number"
                    min={0}
                    value={carnetResidui}
                    onChange={(e) => setCarnetResidui(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Data scadenza</Label>
                  <Input
                    type="date"
                    value={carnetScadenza}
                    onChange={(e) => setCarnetScadenza(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Button variant="outline" onClick={handleSaveCarnet} disabled={savingCarnet}>
                    <Save className="w-4 h-4 mr-1" />
                    {savingCarnet ? "Salvataggio..." : "Aggiorna carnet"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
